import { formatMoney } from "@/shared/lib/defaultCurrency";

export interface TestUiListItem {
  id: string;
  code: string;
  partnerName: string;
  partnerCode: string;
  plant: string;
  productName: string;
  productCode: string;
  lotNo: string;
  storageTemp: string;
  quantity: number;
  uom: string;
  totalAmount: number;
  status: 'Draft' | 'Confirmed' | 'In-progress' | 'Completed' | 'Hold' | 'Cancelled';
  priority: 'Bình thường' | 'Ưu tiên cao' | 'Khẩn cấp';
  dueDate: string;
  createdAt: string;
  assignedTo: string;
}

export interface TestUiLineItem {
  id: string;
  lineNo: number;
  itemCode: string;
  itemName: string;
  spec: string;
  plannedQty: number;
  actualQty: number;
  uom: string;
  unitPrice: number;
  storageCondition: string;
  qcStatus: 'Passed' | 'Pending' | 'Hold';
}

export interface TestUiScenario {
  id: string;
  title: string;
  category: string;
  code: string;
  status: 'Draft' | 'Confirmed' | 'In-progress' | 'Completed' | 'Hold' | 'Cancelled';
  priority: 'Bình thường' | 'Ưu tiên cao' | 'Khẩn cấp';
  partnerName: string;
  partnerCode: string;
  plant: string;
  shift: string;
  targetCompletion: string;
  metrics: Array<{
    id: string;
    label: string;
    value: string;
    hint?: string;
    tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
    progress?: number;
  }>;
  journeySteps: Array<{
    id: string;
    label: string;
    status: 'completed' | 'current' | 'pending' | 'warning';
    time?: string;
  }>;
  attentionItems: Array<{
    id: string;
    severity: 'danger' | 'warning' | 'info';
    title: string;
    detail: string;
    actionLabel?: string;
  }>;
  lines: TestUiLineItem[];
}

export const MOCK_SCENARIOS: Record<string, TestUiScenario> = {
  mes: {
    id: 'mes',
    title: 'Lệnh sản xuất Cá tra Fillet xuất khẩu EU',
    category: 'Sản xuất thực phẩm (MES)',
    code: 'MO-2026-0318',
    status: 'In-progress',
    priority: 'Ưu tiên cao',
    partnerName: 'Hợp tác xã Thủy sản Sông Hậu',
    partnerCode: 'NCC-SH-089',
    plant: 'Phân xưởng Chế biến Đông lạnh STVQ (Sóc Trăng)',
    shift: 'Ca sáng (06:00 - 14:00)',
    targetCompletion: '18:00 Hôm nay',
    metrics: [
      { id: '1', label: 'Sản lượng KH', value: '12.500 kg', hint: 'Fillet size 170-220g', tone: 'neutral' },
      { id: '2', label: 'Thực tế đạt', value: '8.250 kg', hint: '66% tiến độ kế hoạch', tone: 'primary', progress: 66 },
      { id: '3', label: 'Hao hụt sơ chế', value: '2.1%', hint: 'Chuẩn định mức: ≤ 2.4%', tone: 'success' },
      { id: '4', label: 'Nhiệt độ hầm cấp', value: '-38.5°C', hint: 'Đạt chuẩn IQF (-35°C đến -40°C)', tone: 'info' },
      { id: '5', label: 'Nhân sự ca trực', value: '28 CN', hint: '4 chuyền fillet hoạt động', tone: 'neutral' },
      { id: '6', label: 'Kiểm soát CCP-2', value: 'Đạt chuẩn', hint: 'Không phát hiện tạp chất kim loại', tone: 'success' },
    ],
    journeySteps: [
      { id: '1', label: 'Lập lệnh & Dự trù NVL', status: 'completed', time: '28/09 06:15' },
      { id: '2', label: 'Tiếp nhận cá sống & Kiểm dịch', status: 'completed', time: '28/09 07:00' },
      { id: '3', label: 'Fillet & Rửa khử trùng', status: 'completed', time: '28/09 09:30' },
      { id: '4', label: 'Cấp đông nhanh IQF & Mạ băng', status: 'current', time: 'Đang thực hiện' },
      { id: '5', label: 'Đóng thùng & Rà kim loại CCP-2', status: 'pending', time: 'Dự kiến 14:00' },
      { id: '6', label: 'KCS xuất xưởng & Nhập kho lạnh', status: 'pending', time: 'Dự kiến 16:30' },
    ],
    attentionItems: [
      {
        id: 'att-1',
        severity: 'warning',
        title: 'Lô nguyên liệu phụ gia mạ băng còn 48h hạn dùng',
        detail: 'Lô NVL-PG-2026-08 cần hoàn tất sử dụng trước 12:00 ngày 30/09/2026 theo quy chuẩn HACCP.',
        actionLabel: 'Xác nhận lô',
      },
      {
        id: 'att-2',
        severity: 'info',
        title: 'Cần lấy mẫu vi sinh ca sáng lúc 11:30',
        detail: 'Đã hoàn thành 5.000 kg đầu tiên, yêu cầu QA Phòng Lab lấy 3 mẫu thử vi sinh.',
        actionLabel: 'Tạo phiếu mẫu',
      },
    ],
    lines: [
      {
        id: 'line-1',
        lineNo: 1,
        itemCode: 'SP-CT-170220',
        itemName: 'Cá tra fillet đông rời IQF size 170-220g (Thùng 10kg)',
        spec: 'Mạ băng 10%, không xương, không da',
        plannedQty: 7500,
        actualQty: 5100,
        uom: 'kg',
        unitPrice: 68500,
        storageCondition: 'Kho âm ≤ -18°C',
        qcStatus: 'Passed',
      },
      {
        id: 'line-2',
        lineNo: 2,
        itemCode: 'SP-CT-220UP',
        itemName: 'Cá tra fillet đông rời IQF size >220g (Thùng 10kg)',
        spec: 'Mạ băng 10%, không xương, không da',
        plannedQty: 5000,
        actualQty: 3150,
        uom: 'kg',
        unitPrice: 72000,
        storageCondition: 'Kho âm ≤ -18°C',
        qcStatus: 'Passed',
      },
    ],
  },
  sales: {
    id: 'sales',
    title: 'Đơn bán hàng Chuỗi Đại siêu thị Mega Market',
    category: 'Đơn bán hàng (SO)',
    code: 'SO-2026-0922',
    status: 'Confirmed',
    priority: 'Khẩn cấp',
    partnerName: 'Công ty TNHH Mega Market Việt Nam (Chi nhánh An Phú)',
    partnerCode: 'KH-MM-001',
    plant: 'Kho lạnh Trung tâm Cần Thơ (CT-01)',
    shift: 'Đơn hàng kênh Siêu thị hiện đại (MT)',
    targetCompletion: '05:00 Sáng mai (29/09)',
    metrics: [
      { id: '1', label: 'Tổng giá trị', value: formatMoney(485600000), hint: 'Đã bao gồm VAT 8%', tone: 'neutral' },
      { id: '2', label: 'Tổng trọng lượng', value: '4.800 kg', hint: '3 mặt hàng đông lạnh', tone: 'primary' },
      { id: '3', label: 'Tỷ lệ gom hàng', value: '100%', hint: 'Tồn kho khả dụng đủ 100%', tone: 'success', progress: 100 },
      { id: '4', label: 'Nhiệt độ vận chuyển', value: '≤ -18°C', hint: 'Xe tải lạnh chuyên dụng FOTON', tone: 'info' },
      { id: '5', label: 'SLA Giao hàng', value: 'Còn 13 giờ', hint: 'Giao trước 05:00 sáng mai', tone: 'warning' },
      { id: '6', label: 'Chứng nhận ATTP', value: 'ISO 22000 / BRC', hint: 'Đã đính kèm giấy kiểm dịch', tone: 'success' },
    ],
    journeySteps: [
      { id: '1', label: 'Tạo đơn & Chốt giá', status: 'completed', time: '27/09 14:20' },
      { id: '2', label: 'Kế toán duyệt tín dụng', status: 'completed', time: '27/09 15:10' },
      { id: '3', label: 'Giữ hàng tồn FEFO', status: 'completed', time: '27/09 16:00' },
      { id: '4', label: 'Soạn hàng tại kho lạnh', status: 'current', time: 'Đang xếp pallet' },
      { id: '5', label: 'In tem truy xuất & Đóng seal', status: 'pending', time: 'Dự kiến 18:00' },
      { id: '6', label: 'Giao hàng & Ký nhận POD', status: 'pending', time: 'Dự kiến 28/09 05:00' },
    ],
    attentionItems: [
      {
        id: 'att-sales-1',
        severity: 'danger',
        title: 'Xe lạnh giao hàng cần kiểm tra nhiệt độ thùng trước khi bốc hàng',
        detail: 'Quy định đối tác Mega Market: Thùng xe phải đạt nhiệt độ dưới -15°C trước khi cho pallet vào.',
        actionLabel: 'Gửi yêu cầu tài xế',
      },
    ],
    lines: [
      {
        id: 'line-s1',
        lineNo: 1,
        itemCode: 'SP-TS-HOSO',
        itemName: 'Tôm sú nguyên con đông lạnh semi-IQF hộp 1kg (Size 15 con/kg)',
        spec: 'Hộp 1kg, thùng 10 hộp, mạ băng 5%',
        plannedQty: 2000,
        actualQty: 2000,
        uom: 'kg',
        unitPrice: 220000,
        storageCondition: 'Kho lạnh âm sâu -20°C',
        qcStatus: 'Passed',
      },
      {
        id: 'line-s2',
        lineNo: 2,
        itemCode: 'SP-CT-170220',
        itemName: 'Cá tra fillet đông rời IQF size 170-220g (Thùng 10kg)',
        spec: 'Thùng 10kg đóng PE hàn kín',
        plannedQty: 2000,
        actualQty: 2000,
        uom: 'kg',
        unitPrice: 68500,
        storageCondition: 'Kho âm ≤ -18°C',
        qcStatus: 'Passed',
      },
      {
        id: 'line-s3',
        lineNo: 3,
        itemCode: 'SP-MC-TUBE',
        itemName: 'Mực nang ống làm sạch đóng khay 500g',
        spec: 'Khay 500g hút chân không',
        plannedQty: 800,
        actualQty: 800,
        uom: 'kg',
        unitPrice: 195000,
        storageCondition: 'Kho âm ≤ -18°C',
        qcStatus: 'Passed',
      },
    ],
  },
};

export const MOCK_TABLE_ROWS: TestUiListItem[] = [
  {
    id: 'row-1',
    code: 'MO-2026-0318',
    partnerName: 'Hợp tác xã Thủy sản Sông Hậu',
    partnerCode: 'NCC-SH-089',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Cá tra fillet đông rời IQF size 170-220g',
    productCode: 'SP-CT-170220',
    lotNo: 'LOT-2026-F0922-A',
    storageTemp: '-18.5°C',
    quantity: 12500,
    uom: 'kg',
    totalAmount: 856250000,
    status: 'In-progress',
    priority: 'Ưu tiên cao',
    dueDate: '28/09/2026 18:00',
    createdAt: '28/09/2026 06:15',
    assignedTo: 'Nguyễn Văn Hùng (Trưởng ca 1)',
  },
  {
    id: 'row-2',
    code: 'MO-2026-0317',
    partnerName: 'Trang trại Tôm sạch Cà Mau Bio',
    partnerCode: 'NCC-CM-042',
    plant: 'NM Cần Thơ (CT-01)',
    productName: 'Tôm thẻ chân trắng PTO IQF size 31-40',
    productCode: 'SP-TT-PTO3140',
    lotNo: 'LOT-2026-T0921-C',
    storageTemp: '-20.0°C',
    quantity: 6800,
    uom: 'kg',
    totalAmount: 1156000000,
    status: 'Completed',
    priority: 'Bình thường',
    dueDate: '27/09/2026 17:00',
    createdAt: '27/09/2026 07:30',
    assignedTo: 'Trần Thị Mai (KCS Trưởng)',
  },
  {
    id: 'row-3',
    code: 'SO-2026-0922',
    partnerName: 'Công ty TNHH Mega Market Việt Nam',
    partnerCode: 'KH-MM-001',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Hàng đông lạnh hỗn hợp (Tôm + Cá tra + Mực)',
    productCode: 'BUNDLE-SEAFOOD-01',
    lotNo: 'Nhiều lô xuất (Multi-Lot)',
    storageTemp: '-18.0°C',
    quantity: 4800,
    uom: 'kg',
    totalAmount: 485600000,
    status: 'Confirmed',
    priority: 'Khẩn cấp',
    dueDate: '29/09/2026 05:00',
    createdAt: '27/09/2026 14:20',
    assignedTo: 'Lê Hoàng Nam (Sales Sup)',
  },
  {
    id: 'row-4',
    code: 'GRN-2026-0104',
    partnerName: 'Nông trại Thủy sản Mekong Farm',
    partnerCode: 'NCC-MK-015',
    plant: 'Kho NVL Tiếp nhận STVQ',
    productName: 'Cá tra nguyên liệu sống cân tại bè',
    productCode: 'NVL-CA-SONG',
    lotNo: 'LOT-2026-RAW-441',
    storageTemp: 'Hồ sục khí',
    quantity: 25000,
    uom: 'kg',
    totalAmount: 725000000,
    status: 'Hold',
    priority: 'Ưu tiên cao',
    dueDate: '28/09/2026 12:00',
    createdAt: '28/09/2026 08:00',
    assignedTo: 'Phạm Minh Trí (QA Tiếp nhận)',
  },
  {
    id: 'row-5',
    code: 'MO-2026-0319',
    partnerName: 'Hợp tác xã Chế biến Thát lát Hậu Giang',
    partnerCode: 'NCC-HG-022',
    plant: 'Xưởng Chế biến Vĩnh Long (VL-02)',
    productName: 'Chả cá thát lát rút xương tẩm gia vị khay 500g',
    productCode: 'SP-TL-500G',
    lotNo: 'LOT-2026-TL0928',
    storageTemp: '-18.0°C',
    quantity: 3200,
    uom: 'kg',
    totalAmount: 416000000,
    status: 'Draft',
    priority: 'Bình thường',
    dueDate: '30/09/2026 16:00',
    createdAt: '28/09/2026 10:15',
    assignedTo: 'Đỗ Quốc Bảo (Điều độ SX)',
  },
  {
    id: 'row-6',
    code: 'MO-2026-0320',
    partnerName: 'Công ty CP Xuất Nhập Khẩu Thủy Sản An Giang',
    partnerCode: 'NCC-AG-051',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Cá basa cắt khoanh đông lạnh gói 1kg',
    productCode: 'SP-BS-KH500',
    lotNo: 'LOT-2026-BS0929',
    storageTemp: '-18.5°C',
    quantity: 8400,
    uom: 'kg',
    totalAmount: 546000000,
    status: 'In-progress',
    priority: 'Ưu tiên cao',
    dueDate: '29/09/2026 14:00',
    createdAt: '28/09/2026 11:00',
    assignedTo: 'Võ Minh Thuận (Tổ trưởng fillet)',
  },
  {
    id: 'row-7',
    code: 'PO-2026-0812',
    partnerName: 'Tổng kho Bao bì Thực phẩm Tân Á',
    partnerCode: 'NCC-TA-018',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Thùng carton 5 lớp tráng PE chống thấm (10kg)',
    productCode: 'BB-CT-10KG',
    lotNo: 'LOT-BB-2609-01',
    storageTemp: 'Nhiệt độ phòng (≤ 28°C)',
    quantity: 15000,
    uom: 'cái',
    totalAmount: 180000000,
    status: 'Completed',
    priority: 'Bình thường',
    dueDate: '26/09/2026 16:30',
    createdAt: '25/09/2026 09:00',
    assignedTo: 'Đặng Ngọc Lan (Thủ kho NVL)',
  },
  {
    id: 'row-8',
    code: 'MO-2026-0321',
    partnerName: 'Hợp tác xã Nuôi Tôm Công nghệ cao Kiên Giang',
    partnerCode: 'NCC-KG-033',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Tôm sú nguyên con semi-IQF size 12 con/kg',
    productCode: 'SP-TS-12C',
    lotNo: 'LOT-2026-TS0928-B',
    storageTemp: '-20.0°C',
    quantity: 5200,
    uom: 'kg',
    totalAmount: 1404000000,
    status: 'Confirmed',
    priority: 'Khẩn cấp',
    dueDate: '29/09/2026 18:00',
    createdAt: '28/09/2026 13:20',
    assignedTo: 'Nguyễn Văn Hùng (Trưởng ca 1)',
  },
  {
    id: 'row-9',
    code: 'SO-2026-0925',
    partnerName: 'Tập đoàn Bán lẻ Aeon Mall Việt Nam',
    partnerCode: 'KH-AEON-002',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Fillet cá hồi Đại Tây Dương tươi bảo quản MAP',
    productCode: 'SP-CH-MAP400',
    lotNo: 'LOT-2026-CH0929',
    storageTemp: '0°C đến 2°C',
    quantity: 1800,
    uom: 'kg',
    totalAmount: 648000000,
    status: 'In-progress',
    priority: 'Khẩn cấp',
    dueDate: '30/09/2026 06:00',
    createdAt: '28/09/2026 15:00',
    assignedTo: 'Lê Hoàng Nam (Sales Sup)',
  },
  {
    id: 'row-10',
    code: 'QC-2026-0044',
    partnerName: 'Viện Nghiên cứu Nuôi trồng Thủy sản II',
    partnerCode: 'LAB-VN2-01',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Mẫu kiểm nghiệm vi sinh lô cá tra xuất khẩu EU',
    productCode: 'QC-SMP-EU01',
    lotNo: 'SMP-2026-0928',
    storageTemp: 'Tủ mát mẫu 4°C',
    quantity: 24,
    uom: 'mẫu',
    totalAmount: 18500000,
    status: 'Hold',
    priority: 'Ưu tiên cao',
    dueDate: '29/09/2026 11:00',
    createdAt: '28/09/2026 08:30',
    assignedTo: 'Trần Thị Mai (KCS Trưởng)',
  },
  {
    id: 'row-11',
    code: 'MO-2026-0322',
    partnerName: 'Công ty Chế biến Thủy hải sản Bến Tre',
    partnerCode: 'NCC-BT-009',
    plant: 'Xưởng Chế biến Vĩnh Long (VL-02)',
    productName: 'Nghêu lụa hấp đông lạnh hút chân không gói 500g',
    productCode: 'SP-NL-500G',
    lotNo: 'LOT-2026-NL0927',
    storageTemp: '-18.0°C',
    quantity: 4500,
    uom: 'kg',
    totalAmount: 337500000,
    status: 'Completed',
    priority: 'Bình thường',
    dueDate: '27/09/2026 15:00',
    createdAt: '26/09/2026 08:00',
    assignedTo: 'Đỗ Quốc Bảo (Điều độ SX)',
  },
  {
    id: 'row-12',
    code: 'GRN-2026-0108',
    partnerName: 'Hợp tác xã Rau sạch Đà Lạt GAP',
    partnerCode: 'NCC-DL-077',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Bắp ngọt hạt đông lạnh đóng bao 20kg',
    productCode: 'NVL-BNG-20KG',
    lotNo: 'LOT-2026-BNG0925',
    storageTemp: '-18.0°C',
    quantity: 12000,
    uom: 'kg',
    totalAmount: 264000000,
    status: 'Confirmed',
    priority: 'Bình thường',
    dueDate: '28/09/2026 17:00',
    createdAt: '27/09/2026 13:45',
    assignedTo: 'Phạm Minh Trí (QA Tiếp nhận)',
  },
  {
    id: 'row-13',
    code: 'MO-2026-0323',
    partnerName: 'Công ty Thực phẩm Biển Xanh Nha Trang',
    partnerCode: 'NCC-NT-014',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Mực ống câu đông rời IQF size 20-25cm',
    productCode: 'SP-MC-2025',
    lotNo: 'LOT-2026-MC0929',
    storageTemp: '-22.0°C',
    quantity: 3600,
    uom: 'kg',
    totalAmount: 828000000,
    status: 'In-progress',
    priority: 'Ưu tiên cao',
    dueDate: '30/09/2026 12:00',
    createdAt: '28/09/2026 09:15',
    assignedTo: 'Võ Minh Thuận (Tổ trưởng fillet)',
  },
  {
    id: 'row-14',
    code: 'SO-2026-0928',
    partnerName: 'Hệ thống Siêu thị WinMart Miền Nam',
    partnerCode: 'KH-WM-012',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Tôm thẻ tẩm bột xù Tempura khay 300g',
    productCode: 'SP-TT-TEMP300',
    lotNo: 'LOT-2026-TP0926',
    storageTemp: '-18.0°C',
    quantity: 6500,
    uom: 'kg',
    totalAmount: 975000000,
    status: 'Confirmed',
    priority: 'Khẩn cấp',
    dueDate: '29/09/2026 21:00',
    createdAt: '28/09/2026 07:00',
    assignedTo: 'Lê Hoàng Nam (Sales Sup)',
  },
  {
    id: 'row-15',
    code: 'MO-2026-0324',
    partnerName: 'Trang trại Nuôi trồng Thủy sản Đồng Tháp',
    partnerCode: 'NCC-DT-065',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Cá rô phi fillet làm sạch đông lạnh xuất khẩu Mỹ',
    productCode: 'SP-RP-US01',
    lotNo: 'LOT-2026-RP0928',
    storageTemp: '-18.5°C',
    quantity: 18000,
    uom: 'kg',
    totalAmount: 1170000000,
    status: 'Draft',
    priority: 'Bình thường',
    dueDate: '02/10/2026 17:00',
    createdAt: '28/09/2026 14:00',
    assignedTo: 'Nguyễn Văn Hùng (Trưởng ca 1)',
  },
  {
    id: 'row-16',
    code: 'GRN-2026-0112',
    partnerName: 'Công ty Hóa chất Phụ gia Thực phẩm Kim Biên',
    partnerCode: 'NCC-KB-003',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Sodium Tripolyphosphate thực phẩm bao 25kg (STPP)',
    productCode: 'NVL-STPP-25',
    lotNo: 'LOT-CHEM-2609',
    storageTemp: 'Khô ráo (≤ 30°C)',
    quantity: 2500,
    uom: 'kg',
    totalAmount: 112500000,
    status: 'Completed',
    priority: 'Bình thường',
    dueDate: '27/09/2026 11:30',
    createdAt: '26/09/2026 10:00',
    assignedTo: 'Đặng Ngọc Lan (Thủ kho NVL)',
  },
  {
    id: 'row-17',
    code: 'MO-2026-0325',
    partnerName: 'Hợp tác xã Thủy sản Sông Hậu',
    partnerCode: 'NCC-SH-089',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Cá tra cuộn hoa hồng tẩm sốt cay đông lạnh khay 400g',
    productCode: 'SP-CT-ROSE400',
    lotNo: 'LOT-2026-CT0929-R',
    storageTemp: '-18.0°C',
    quantity: 2800,
    uom: 'kg',
    totalAmount: 392000000,
    status: 'In-progress',
    priority: 'Bình thường',
    dueDate: '30/09/2026 15:30',
    createdAt: '28/09/2026 16:00',
    assignedTo: 'Trần Thị Mai (KCS Trưởng)',
  },
  {
    id: 'row-18',
    code: 'SO-2026-0930',
    partnerName: 'Chuỗi Nhà hàng Golden Gate Group',
    partnerCode: 'KH-GGG-005',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Mực nút (Baby Octopus) làm sạch đông rời IQF',
    productCode: 'SP-BO-BABY',
    lotNo: 'LOT-2026-BO0927',
    storageTemp: '-20.0°C',
    quantity: 4200,
    uom: 'kg',
    totalAmount: 882000000,
    status: 'Confirmed',
    priority: 'Ưu tiên cao',
    dueDate: '29/09/2026 10:00',
    createdAt: '27/09/2026 16:45',
    assignedTo: 'Lê Hoàng Nam (Sales Sup)',
  },
  {
    id: 'row-19',
    code: 'MO-2026-0326',
    partnerName: 'Trang trại Tôm sạch Cà Mau Bio',
    partnerCode: 'NCC-CM-042',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Tôm thẻ lột vỏ còn đuôi CPTO khay 500g',
    productCode: 'SP-TT-CPTO500',
    lotNo: 'LOT-2026-TT0929-D',
    storageTemp: '-20.0°C',
    quantity: 7500,
    uom: 'kg',
    totalAmount: 1387500000,
    status: 'Hold',
    priority: 'Khẩn cấp',
    dueDate: '01/10/2026 18:00',
    createdAt: '28/09/2026 11:30',
    assignedTo: 'Phạm Minh Trí (QA Tiếp nhận)',
  },
  {
    id: 'row-20',
    code: 'MO-2026-0327',
    partnerName: 'Hợp tác xã Chế biến Thát lát Hậu Giang',
    partnerCode: 'NCC-HG-022',
    plant: 'Xưởng Chế biến Vĩnh Long (VL-02)',
    productName: 'Viên chả cá thát lát thì là đông lạnh gói 250g',
    productCode: 'SP-TL-BALL250',
    lotNo: 'LOT-2026-TL0929',
    storageTemp: '-18.0°C',
    quantity: 2100,
    uom: 'kg',
    totalAmount: 294000000,
    status: 'Draft',
    priority: 'Bình thường',
    dueDate: '03/10/2026 14:00',
    createdAt: '28/09/2026 15:30',
    assignedTo: 'Đỗ Quốc Bảo (Điều độ SX)',
  },
  {
    id: 'row-21',
    code: 'MO-2026-0328',
    partnerName: 'Công ty Cổ phần Thủy sản Minh Phú',
    partnerCode: 'NCC-MP-001',
    plant: 'NM Sóc Trăng (STVQ)',
    productName: 'Tôm sú sinh thái rừng ngập mặn size 8-10 con/kg',
    productCode: 'SP-TS-ECO0810',
    lotNo: 'LOT-2026-TS0930-ECO',
    storageTemp: '-22.0°C',
    quantity: 3400,
    uom: 'kg',
    totalAmount: 1190000000,
    status: 'Confirmed',
    priority: 'Ưu tiên cao',
    dueDate: '01/10/2026 12:00',
    createdAt: '29/09/2026 07:15',
    assignedTo: 'Nguyễn Văn Hùng (Trưởng ca 1)',
  },
  {
    id: 'row-22',
    code: 'SO-2026-0932',
    partnerName: 'Tập đoàn Central Retail (Go! / BigC)',
    partnerCode: 'KH-CR-007',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Cá hồi xông khói Na Uy cắt lát gói 100g',
    productCode: 'SP-CH-SMK100',
    lotNo: 'LOT-2026-SMK0929',
    storageTemp: '0°C đến 4°C',
    quantity: 1200,
    uom: 'kg',
    totalAmount: 780000000,
    status: 'Completed',
    priority: 'Bình thường',
    dueDate: '29/09/2026 16:00',
    createdAt: '28/09/2026 08:30',
    assignedTo: 'Lê Hoàng Nam (Sales Sup)',
  },
  {
    id: 'row-23',
    code: 'GRN-2026-0118',
    partnerName: 'Công ty Muối Thực phẩm Bạc Liêu',
    partnerCode: 'NCC-BL-008',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Muối tinh khiết sấy khô bao 50kg (HACCP)',
    productCode: 'NVL-MUI-50',
    lotNo: 'LOT-SALT-2609',
    storageTemp: 'Khô thoáng (≤ 32°C)',
    quantity: 30000,
    uom: 'kg',
    totalAmount: 120000000,
    status: 'Completed',
    priority: 'Bình thường',
    dueDate: '27/09/2026 17:00',
    createdAt: '26/09/2026 14:00',
    assignedTo: 'Đặng Ngọc Lan (Thủ kho NVL)',
  },
  {
    id: 'row-24',
    code: 'MO-2026-0329',
    partnerName: 'Hợp tác xã Nuôi Ếch Đồng Tháp',
    partnerCode: 'NCC-ECH-019',
    plant: 'Xưởng Chế biến Vĩnh Long (VL-02)',
    productName: 'Đùi ếch làm sạch đông rời IQF size 6-8 cặp/kg',
    productCode: 'SP-ECH-0608',
    lotNo: 'LOT-2026-ECH0929',
    storageTemp: '-18.0°C',
    quantity: 2600,
    uom: 'kg',
    totalAmount: 312000000,
    status: 'In-progress',
    priority: 'Bình thường',
    dueDate: '30/09/2026 17:00',
    createdAt: '29/09/2026 08:00',
    assignedTo: 'Võ Minh Thuận (Tổ trưởng fillet)',
  },
  {
    id: 'row-25',
    code: 'MO-2026-0330',
    partnerName: 'Trang trại Bò Thịt Vĩnh Gia Farm',
    partnerCode: 'NCC-BO-099',
    plant: 'Kho Tổng Cần Thơ (CT-01)',
    productName: 'Thịt bò Úc cắt lát đông lạnh khay 250g',
    productCode: 'SP-BO-SL250',
    lotNo: 'LOT-2026-BO0930',
    storageTemp: '-18.0°C',
    quantity: 3100,
    uom: 'kg',
    totalAmount: 992000000,
    status: 'Draft',
    priority: 'Khẩn cấp',
    dueDate: '02/10/2026 11:00',
    createdAt: '29/09/2026 09:30',
    assignedTo: 'Phạm Minh Trí (QA Tiếp nhận)',
  },
];

export const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "Tất cả trạng thái" },
  { value: "In-progress", label: "Đang sản xuất (In-progress)" },
  { value: "Confirmed", label: "Đã xác nhận (Confirmed)" },
  { value: "Completed", label: "Hoàn tất xuất xưởng (Completed)" },
  { value: "Hold", label: "Tạm giữ KCS (Hold)" },
  { value: "Draft", label: "Bản nháp (Draft)" },
];

export const PLANT_FILTER_OPTIONS = [
  { value: "all", label: "Tất cả phân xưởng" },
  { value: "STVQ", label: "NM Thủy sản Sóc Trăng (STVQ)" },
  { value: "CT-01", label: "Kho Lạnh Tổng Cần Thơ (CT-01)" },
  { value: "VL-02", label: "Xưởng Chế biến Vĩnh Long (VL-02)" },
];

